import { SxProps, Theme } from '@mui/material';

// Shared style for the left-hand label of each summary row ("Čas", "Cena", …).
export const headSx: SxProps = {
  gridColumn: { xs: 'span 96', lg: 'auto' },
  fontSize: { xs: 12, lg: 14 },
  lineHeight: { xs: '12px', lg: '24px' },
  mt: { xs: 0.75, lg: 0 },
  mb: { xs: 0.25, lg: 0 },
  pr: { xs: 0, lg: 1 },
  width: 'auto',
};

// CSS-only tooltip: any element carrying a `data-tooltip` attribute shows a
// bubble (time on the first line, price on the second) above it on hover. The
// text comes from the attribute via `content: attr(...)`, so no JS is involved.
// Match the look of the old chart <CustomTooltip>: dark blue-grey bubble,

// white text, rounded corners.
const tooltipBg = (theme: Theme) =>
  theme.palette.mode === 'light' ? theme.palette.text.disabled : '#343e42';

export const tooltipSx = {
  '& [data-tooltip]': { position: 'relative' },
  '& [data-tooltip]:hover::after': {
    content: 'attr(data-tooltip)',
    position: 'absolute',
    left: '50%',
    bottom: 'calc(100% + 8px)',
    // Slide the bubble horizontally by its own width as we move across the grid:
    // 0% at the left edge (left edge of bubble sits at the bar), 100% at the
    // right edge (right edge at the bar), 50% in the middle. `--tip-shift` is set
    // per column below, so edge tooltips never overflow the page. The arrow
    // (::before) stays centered on the bar regardless.
    transform: 'translateX(calc(-1 * var(--tip-shift, 50%)))',
    px: { xs: 1.5, md: 2 },
    py: 1,
    borderRadius: 2,
    backgroundColor: tooltipBg,
    color: 'common.white',
    fontSize: 12,
    fontWeight: 600,
    lineHeight: 1.35,
    textAlign: 'center',
    whiteSpace: 'pre',
    zIndex: 30,
    pointerEvents: 'none',
  },
  '& [data-tooltip]:hover::before': {
    content: '""',
    position: 'absolute',
    left: '50%',
    bottom: 'calc(100% + 3px)',
    transform: 'translateX(-50%)',
    borderWidth: '5px',
    borderStyle: 'solid',
    borderColor: 'transparent',
    borderTopColor: tooltipBg,
    zIndex: 30,
    pointerEvents: 'none',
  },
  // Chart columns are full-chart-height boxes, so anchoring the bubble above
  // them floats it far above the bar. Center it vertically inside the column
  // instead (the same `--tip-shift` still slides it horizontally), and drop the
  // downward arrow since there's no longer an element edge for it to point at.
  '& .chart-overlay [data-tooltip]:hover::after': {
    top: '50%',
    bottom: 'auto',
    transform: 'translate(calc(-1 * var(--tip-shift, 50%)), -50%)',
  },
  '& .chart-overlay [data-tooltip]:hover::before': {
    display: 'none',
  },
};

export const hoverGroupSx: Record<string, { opacity: number }> = {};
export const overlayGroupSx: Record<string, { backgroundColor: string }> = {};

// Per-column horizontal anchor for the tooltip bubble: 0% for the first column
// up to 100% for the last, linearly. Read by the `::after` transform above so
// the bubble drifts from left-aligned to right-aligned across the grid and
// never spills off the page — read more there. Both the chart bars and the data
// cells share the `spot-summary-col-{period}-{i}` class, so this covers both.
export const tipShiftSx: Record<string, { '--tip-shift': string }> = {};
const addTipShifts = (period: '60M' | '15M', count: number) => {
  for (let i = 0; i < count; i++) {
    tipShiftSx[`& .spot-summary-col-${period}-${i}`] = {
      '--tip-shift': `${(i / (count - 1)) * 100}%`,
    };
  }
};
addTipShifts('60M', 24);
addTipShifts('15M', 96);

const lightDataRows = (trigger: string, targets: string) => {
  hoverGroupSx[`&:has(${trigger}:hover) ${targets}`] = { opacity: 1 };
};
// keep the matching chart bar(s) clear (no white veil) while hovered. The veil
// uses background-color (not opacity) so the bars stay at opacity 1 and their
// `data-tooltip` bubbles remain visible even on the revealed/hovered bar.
const clearChartBars = (trigger: string, targets: string) => {
  overlayGroupSx[`&:has(${trigger}:hover) .chart-overlay ${targets}`] = {
    backgroundColor: 'transparent !important',
  };
};

// 60M trigger: hovering an hour cell (or, in 60M mode, an hour-wide chart bar)
// highlights that hour's 60M cells plus all four 15M bars within the hour.
for (let h = 0; h < 24; h++) {
  const trigger = `.spot-summary-col-60M-${h}`;
  const hourTargets = `:is(.spot-summary-col-60M-${h}, .spot-summary-col-15M-${
    h * 4
  }, .spot-summary-col-15M-${h * 4 + 1}, .spot-summary-col-15M-${
    h * 4 + 2
  }, .spot-summary-col-15M-${h * 4 + 3})`;
  lightDataRows(trigger, hourTargets);
  clearChartBars(trigger, hourTargets);
}

// 15M trigger: hovering a single 15M bar highlights only that bar and its hour's
// 60M cells (the nearest 60M label/level) — never the sibling 15M bars.
for (let j = 0; j < 96; j++) {
  const trigger = `.spot-summary-col-15M-${j}`;
  const segTargets = `:is(.spot-summary-col-15M-${j}, .spot-summary-col-60M-${Math.floor(
    j / 4
  )})`;
  lightDataRows(trigger, segTargets);
  clearChartBars(trigger, segTargets);
}
